import { Box, Chip, Stack, Typography } from '@mui/material';
import { useHeroPalette } from '../lib/palette/use-hero-palette';
import { PlatformIcons } from './PlatformIcons';

/**
 * `<GameHero>` — 16:9 landscape hero at the top of the Game page.
 *
 * Layout:
 *   - Full-width 16:9 image fills the hero slot.
 *   - A gradient fades from transparent (top) → semi-opaque
 *     (bottom-left) so the title + release date + platform chips
 *     overlaid on the lower-left read cleanly against any image.
 *   - `useHeroPalette` extracts a dominant color + text-contrast
 *     hint from the loaded image on the client; those values tune
 *     the gradient tint and the text color.
 *
 * When `imageUrl` is empty / missing, the hero collapses to a
 * title-only header with the brand's neutral background.
 *
 * R3.7 — every function ≤20 non-comment lines.
 */
import type { GameMediaItem } from '../lib/api/games-api-client';

interface GameHeroProps {
  readonly title: string;
  readonly hero: GameMediaItem;
  readonly releaseDate: string;
  readonly platforms: ReadonlyArray<string>;
}

/** Format an ISO-8601 calendar date as `MMMM d, yyyy` in UTC. */
function formatReleaseDate(isoDate: string): string {
  if (isoDate.length === 0) return '';
  const formatter = new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
  });
  const parsed = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return formatter.format(parsed);
}

export function GameHero({ title, hero, releaseDate, platforms }: GameHeroProps): JSX.Element {
  const imageUrl = hero.url;
  const palette = useHeroPalette(imageUrl.length > 0 ? imageUrl : undefined);
  const textColor = palette?.textOnHero === 'dark' ? '#111' : '#fff';
  const gradientTint = palette?.dominant ?? '#000000';
  if (imageUrl.length === 0) return <HeroFallback title={title} releaseDate={releaseDate} platforms={platforms} />;
  return (
    <Box sx={{ position: 'relative', aspectRatio: '16 / 9', width: '100%', overflow: 'hidden', borderRadius: 2 }}>
      <Box component="img" src={imageUrl} alt={title}
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      <Box sx={{
        position: 'absolute', inset: 0,
        background: `linear-gradient(to bottom, transparent 0%, transparent 40%, ${alpha(gradientTint, 0.85)} 100%)`,
      }} />
      <HeroOverlay title={title} releaseDate={releaseDate} platforms={platforms} textColor={textColor} />
    </Box>
  );
}

interface HeroOverlayProps {
  readonly title: string;
  readonly releaseDate: string;
  readonly platforms: ReadonlyArray<string>;
  readonly textColor: string;
}

/** Title + featured release-date chip + platform icons overlaid on the hero. */
function HeroOverlay({ title, releaseDate, platforms, textColor }: HeroOverlayProps): JSX.Element {
  const dateLabel = formatReleaseDate(releaseDate);
  return (
    <Stack spacing={1.5} sx={{
      position: 'absolute', left: { xs: 16, sm: 32 }, right: { xs: 16, sm: 32 },
      bottom: { xs: 16, sm: 32 }, color: textColor,
    }}>
      <Typography variant="h1" component="h1" sx={{
        fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
        fontWeight: 700, lineHeight: 1.1, textShadow: '0 2px 12px rgba(0,0,0,0.4)',
      }}>{title}</Typography>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        {dateLabel && <Chip label={dateLabel} size="medium"
          sx={{ fontSize: '0.95rem', fontWeight: 500, bgcolor: 'rgba(255,255,255,0.9)', color: '#111' }} />}
        <Box sx={{ color: textColor }}><PlatformIcons platforms={platforms} /></Box>
      </Stack>
    </Stack>
  );
}

interface HeroFallbackProps {
  readonly title: string;
  readonly releaseDate: string;
  readonly platforms: ReadonlyArray<string>;
}

/** Title-only fallback rendered when the game has no 16:9 hero yet. */
function HeroFallback({ title, releaseDate, platforms }: HeroFallbackProps): JSX.Element {
  return (
    <Stack spacing={2} sx={{ py: 3 }}>
      <Typography variant="h1" component="h1" sx={{
        fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }, fontWeight: 700, lineHeight: 1.1,
      }}>{title}</Typography>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        {releaseDate.length > 0 && <Chip label={formatReleaseDate(releaseDate)} />}
        <PlatformIcons platforms={platforms} />
      </Stack>
    </Stack>
  );
}

/** Minimal `#rrggbb` → `rgba(r, g, b, a)` helper so the gradient uses the extracted dominant hue. */
function alpha(hex: string, a: number): string {
  const sanitized = hex.replace(/^#/, '');
  const r = parseInt(sanitized.slice(0, 2), 16);
  const g = parseInt(sanitized.slice(2, 4), 16);
  const b = parseInt(sanitized.slice(4, 6), 16);
  if ([r, g, b].some((v) => Number.isNaN(v))) return `rgba(0,0,0,${a})`;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}
